import { DetailsPanel } from "../ui";
import { useMemo, useRef, useState, type KeyboardEvent } from "react";

import type { SessionResultOut } from "../contract/api";
import type { AnalyticsNotesStyle } from "../contract/analytics";
import { captureAnalytics } from "../analytics";
import { t } from "../contract/strings";
import {
  Button,
  CheckIcon,
  ChevronRightIcon,
  Skeleton,
  SkeletonLine,
  SyncIcon,
  WarningIcon,
  cx,
} from "../ui";
import { ActionsCard, MomentLink } from "./result-actions";
import type { ActionTicks } from "./result-actions-state";
import { HIT_CHIP, LABEL, SECTION_GAP } from "./result-model";
import {
  initialStyle,
  notesRows,
  outlineChildren,
  say,
  styleLabel,
  visibleOutline,
  type NoteRows,
  type NoteSectionRow,
  type OutlineRow,
  type StyleChipRow,
} from "./result-notes-model";
import type { MeetingNotes } from "./result-notes-state";

/** How a style's notes are shown: as headed sections, or as the outline's tree. */
export type NotesView = "list" | "map";

/** The styles the shared analytics enum knows; a server-added style is simply not counted. */
const COUNTED_STYLES: ReadonlySet<string> = new Set([
  "meeting",
  "client_call",
  "interview",
  "lecture",
  "workshop",
  "one_to_one",
]);

function countedStyle(style: string): AnalyticsNotesStyle | null {
  return COUNTED_STYLES.has(style) ? (style as AnalyticsNotesStyle) : null;
}

/** The address's `?view=`: `map` is the mind map, anything else the list. */
export function viewFromParam(value: string | null): NotesView {
  return value === "map" ? "map" : "list";
}

const CHIP_BUTTON =
  // Style names in their own case, like the transcript's speaker chips: no caps tracking.
  "font-code text-count font-semibold border rounded-pill px-chip-x py-1 cursor-pointer transition-colors inline-flex items-center gap-1";

/** A card on this tab: the same white card as Quick's summary. */
const CARD = "p-4 bg-surface border border-line rounded-card flex flex-col gap-stack";

/**
 * Detailed: the meeting's notes in a style (#774, phase 3 of #763).
 *
 * The style chips are the server's list (`GET /v1/sessions/{id}/notes`), each with a tick
 * once notes have been written in it. Tapping a style shows its notes; a style nobody has
 * asked for is asked for there and then ("Writing your notes…", polled until ready). Notes
 * are shared: one set per style for everyone in the meeting. The open style and the view
 * are in the address (`?style=`, `?view=map`), so Back, a reload and a shared link keep
 * them.
 */
export function DetailedTab({
  result,
  notes,
  ticks,
  style,
  view,
  onStyle,
  onView,
  onJump,
}: {
  /** The meeting result: its commitments are the actions a style shows. */
  result: SessionResultOut;
  notes: MeetingNotes;
  /** The meeting's shared action ticks (#786), the same Quick shows. */
  ticks: ActionTicks;
  style: string | null;
  view: NotesView;
  onStyle: (style: string) => void;
  onView: (view: NotesView) => void;
  onJump: (time: number) => void;
}) {
  const rows = useMemo(() => (notes.data ? notesRows(notes.data) : null), [notes.data]);

  // Loading, the tab is a skeleton, drawn by the gate around it (`result.tsx`, #1043); a
  // failure is the card with its line and Try again.
  if (!rows) {
    if (notes.problem !== "load_failed") return null;
    return (
      <section className={CARD} aria-live="polite">
        <span className={cx(LABEL, "text-label text-ink-soft")}>{t("result.notes_style")}</span>
        <p className="m-0 text-note text-ink">{t("result.notes_load_failed")}</p>
        <div>
          <Button variant="ghost" onClick={() => {
            captureAnalytics("retry_tapped", { surface: "notes_write" });
            notes.reload();
          }}>
            {t("result.try_again")}
          </Button>
        </div>
      </section>
    );
  }

  const known = rows.chips.some((chip) => chip.key === style);
  const open = known ? style : initialStyle(rows);
  const chip = rows.chips.find((c) => c.key === open) ?? null;
  const note = rows.notes.find((n) => n.style === open) ?? null;

  function choose(next: StyleChipRow): void {
    const counted = countedStyle(next.key);
    if (counted) captureAnalytics("result_notes_style_selected", { style: counted });
    onStyle(next.key);
    if (next.state === "idle") void notes.ask(next.key);
  }

  return (
    <div className={cx("flex flex-col", SECTION_GAP)}>
      <section className={CARD}>
        <span className={cx(LABEL, "text-label text-ink-soft")}>{t("result.notes_style")}</span>
        <div
          role="group"
          aria-label={t("result.notes_styles_aria")}
          // Rows 12 apart with 32-unit touch boxes that do not overlap (HIT_CHIP).
          className="flex flex-wrap gap-x-tight gap-y-3 py-1"
        >
          {rows.chips.map((c) => {
            const on = c.key === open;
            const label = styleLabel(c);
            return (
              <button
                key={c.key}
                type="button"
                data-notes-style={c.key}
                data-state={c.state}
                aria-pressed={on}
                aria-label={c.written ? t("result.notes_style_written_aria", { style: label }) : label}
                disabled={!rows.enabled || notes.asking === c.key}
                onClick={() => choose(c)}
                className={cx(
                  CHIP_BUTTON,
                  HIT_CHIP,
                  on
                    ? "bg-accent text-accent-ink border-accent"
                    : "bg-surface text-ink border-line hover:border-accent",
                  "disabled:opacity-60 disabled:cursor-not-allowed",
                )}
              >
                {c.written ? <CheckIcon data-written className="size-icon-xs flex-none" /> : null}
                {label}
              </button>
            );
          })}
        </div>
        {!rows.enabled ? (
          <p className="m-0 text-note text-ink-soft">{t("result.notes_unavailable")}</p>
        ) : notes.problem === "rate_limited" ? (
          <p role="status" className="m-0 text-note text-warn-ink">
            {t("result.notes_rate_limited")}
          </p>
        ) : notes.problem === "ask_failed" && !note ? (
          <p role="status" className="m-0 text-note text-danger">
            {t("result.notes_failed")}
          </p>
        ) : !chip ? (
          <p className="m-0 text-note text-ink-soft">{t("result.notes_pick")}</p>
        ) : null}
      </section>

      {chip && note ? (
        <StyleNotes
          key={chip.key}
          note={note}
          busy={notes.asking === chip.key}
          view={view}
          onView={onView}
          onJump={onJump}
          onUpdate={() => void notes.ask(chip.key, true)}
          onRetry={() => {
            captureAnalytics("retry_tapped", { surface: "notes_write" });
            void notes.ask(chip.key);
          }}
          stalled={notes.stalled}
          onCheckAgain={() => {
            captureAnalytics("retry_tapped", { surface: "notes_write" });
            notes.reload();
          }}
        />
      ) : chip && notes.asking === chip.key ? (
        <WritingCard updating={false} />
      ) : null}

      {/*
        The meeting's actions (#774): not written by the notes but the result's own
        commitments -- Quick's Actions card itself, with its ticks and counter, which
        the meeting shares (#786), so a tick on either tab shows on the other. Grouped
        by owner for the styles that list "commitments on each side". Not in the mind
        map, which is the outline alone.
      */}
      {chip?.commitments && !(view === "map" && (note?.outline.length ?? 0) > 0) ? (
        <ActionsCard
          key={`${result.session_id}:${chip.key}`}
          result={result}
          ticks={ticks}
          onJump={onJump}
          groupBy={chip.commitments === "by_person" ? "person" : undefined}
        />
      ) : null}
    </div>
  );
}

/**
 * The styles card while the notes load (#1043): its label, the style chips and the line
 * under them, as static blocks. Each chip is the chip's box (one `text-count` line, `py-1`
 * and the border), and the six are about the catalogue's widths, so they wrap into the
 * rows the real chips do (#1049). The gate around the tab draws it (`result.tsx`).
 */
export function NotesSkeleton() {
  return (
    <div className={CARD}>
      <SkeletonLine size="label" width="w-tile-lg" />
      <div className="flex flex-wrap gap-x-tight gap-y-3 py-1">
        {["w-28", "w-tile-lg", "w-tile-sm", "w-28", "w-tile-sm", "w-tile-lg"].map((width, i) => (
          <Skeleton
            key={i}
            className={cx("box-content h-lh py-1 border border-transparent text-count rounded-pill", width)}
          />
        ))}
      </div>
      <SkeletonLine size="note" width="w-1/2" />
    </div>
  );
}

/**
 * The status card while notes are written. Once the poll's patience has run out it stops
 * turning and says so plainly, with a way to look again, rather than spinning for ever.
 */
function WritingCard({
  updating,
  stalled = false,
  onCheckAgain,
}: {
  updating: boolean;
  stalled?: boolean;
  onCheckAgain?: () => void;
}) {
  return (
    <section data-notes-writing role="status" aria-live="polite" className={CARD}>
      <span className="flex items-center gap-tight">
        <SyncIcon
          className={cx(
            "size-icon-xs text-accent flex-none",
            !stalled && "animate-spin motion-reduce:animate-none",
          )}
        />
        <span className="font-title text-person font-semibold tracking-headline text-ink">
          {t(updating ? "result.notes_updating" : "result.notes_writing")}
        </span>
      </span>
      {stalled ? (
        <>
          <p data-notes-taking-long className="m-0 text-note text-ink">
            {t("result.notes_taking_long")}
          </p>
          <div>
            <Button variant="ghost" onClick={onCheckAgain}>
              {t("result.check_again")}
            </Button>
          </div>
        </>
      ) : updating ? null : (
        <p className="m-0 text-note text-ink-soft">{t("result.notes_writing_note")}</p>
      )}
    </section>
  );
}

/** One style's notes in whatever state they are in. */
function StyleNotes({
  note,
  busy,
  view,
  onView,
  onJump,
  onUpdate,
  onRetry,
  stalled,
  onCheckAgain,
}: {
  note: NoteRows;
  busy: boolean;
  view: NotesView;
  onView: (view: NotesView) => void;
  onJump: (time: number) => void;
  onUpdate: () => void;
  onRetry: () => void;
  stalled: boolean;
  onCheckAgain: () => void;
}) {
  if (note.state === "writing") {
    return <WritingCard updating={false} stalled={stalled} onCheckAgain={onCheckAgain} />;
  }
  if (note.state === "failed") {
    return (
      <section data-notes-failed role="status" className={cx(CARD, "border-danger/40")}>
        <p className="m-0 text-note text-ink">{note.status ? say(note.status) : null}</p>
        <div>
          <Button variant="ghost" disabled={busy} onClick={onRetry}>
            {t("result.try_again")}
          </Button>
        </div>
      </section>
    );
  }

  const hasMap = note.outline.length > 0;
  const shown = hasMap ? view : "list";
  return (
    <div className={cx("flex flex-col", SECTION_GAP)} lang={note.language ?? undefined}>
      {note.state === "updating" ? (
        <WritingCard updating stalled={stalled} onCheckAgain={onCheckAgain} />
      ) : null}
      {note.state === "out_of_date" || note.state === "update_failed" ? (
        <section
          data-notes-stale={note.state}
          role="status"
          className="p-4 bg-surface-3 border border-warn-ink/40 rounded-card flex flex-col gap-stack"
        >
          <span className="flex items-start gap-tight">
            <WarningIcon className="size-icon-xs text-warn-ink flex-none mt-0.5" />
            <span className="text-note text-ink">{note.status ? say(note.status) : null}</span>
          </span>
          <DetailsPanel summary={t("result.notes_history")}><p>{t("result.notes_history_detail")}</p></DetailsPanel>
          <div>
            {note.can_update ? (
              <Button variant="ghost" disabled={busy} onClick={onUpdate}>
                {t("result.notes_update")}
              </Button>
            ) : note.can_retry ? (
              <Button variant="ghost" disabled={busy} onClick={onRetry}>
                {t("result.try_again")}
              </Button>
            ) : null}
          </div>
        </section>
      ) : null}

      {hasMap ? <ViewToggle value={shown} onChange={onView} /> : null}
      {shown === "map" ? (
        <MindMap rows={note.outline} />
      ) : (
        note.sections.map((section, index) => (
          <SectionCard key={`${section.key}-${index}`} section={section} onJump={onJump} />
        ))
      )}
    </div>
  );
}

function ViewToggle({ value, onChange }: { value: NotesView; onChange: (view: NotesView) => void }) {
  const views: Array<[NotesView, string]> = [
    ["list", t("result.notes_view_list")],
    ["map", t("result.notes_view_map")],
  ];
  return (
    <div
      role="group"
      aria-label={t("result.notes_view_aria")}
      className="self-start inline-flex gap-nudge p-nudge bg-surface-2 border border-line rounded-card"
    >
      {views.map(([id, label]) => (
        <button
          key={id}
          type="button"
          data-notes-view={id}
          aria-pressed={value === id}
          onClick={() => {
            captureAnalytics("result_notes_view_selected", { view: id });
            onChange(id);
          }}
          className={cx(
            "font-code font-bold uppercase tracking-name text-count",
            "min-h-icon-button px-3 rounded-control border cursor-pointer transition-colors",
            "bg-transparent border-transparent text-ink-soft hover:text-accent",
            "aria-pressed:bg-surface aria-pressed:border-line-strong aria-pressed:text-ink",
          )}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

/** One headed block of notes. The meeting's actions are not here: see `ActionsCard`. */
function SectionCard({ section, onJump }: { section: NoteSectionRow; onJump: (time: number) => void }) {
  return (
    <section
      data-notes-section={section.key}
      className={CARD}
      aria-label={section.heading || undefined}
    >
      {section.heading ? (
        <h3 className="m-0 flex items-center gap-tight">
          <span className={cx(LABEL, "text-label text-ink-soft")}>{section.heading}</span>
        </h3>
      ) : null}
      {section.body
        ? section.body.split("\n\n").map((paragraph, index) => (
            <p key={index} className="m-0 text-summary text-ink">
              {paragraph}
            </p>
          ))
        : null}
      {section.bullets.length ? (
        <ul className="m-0 p-0 list-none flex flex-col gap-nudge">
          {section.bullets.map((bullet, index) => (
            <li key={index} className="flex items-baseline gap-tight text-note leading-normal text-ink">
              <span aria-hidden="true" className="flex-none font-bold text-accent">
                •
              </span>
              <span className="min-w-0">{bullet}</span>
            </li>
          ))}
        </ul>
      ) : null}
      {section.quotes.map((quote, index) => (
        <figure
          key={index}
          data-notes-quote
          aria-label={t("result.quote_aria", { name: quote.speaker })}
          className="relative m-0 p-2 pr-8 bg-surface-2 border border-line rounded-control flex flex-col gap-nudge"
        >
          <blockquote className="m-0 text-note leading-normal text-ink italic">“{quote.text}”</blockquote>
          <figcaption className="text-count text-ink-soft">{quote.speaker}</figcaption>
          <MomentLink data-notes-quote-jump at={quote.source_start} onJump={onJump} />
        </figure>
      ))}
    </section>
  );
}

/**
 * The mind map: the notes' outline as a collapsible tree, never an image (#774).
 *
 * A WAI-ARIA tree with one tab stop: the arrow keys move through the visible items, Right
 * opens a closed branch (or steps into an open one), Left closes it (or steps out to its
 * parent), Home and End jump to the ends, and Enter or Space opens and closes. Everything
 * starts open; the outline is three levels at most.
 */
export function MindMap({ rows }: { rows: OutlineRow[] }) {
  const children = useMemo(() => outlineChildren(rows), [rows]);
  const [expanded, setExpanded] = useState<Set<string>>(
    () => new Set(rows.filter((row) => row.has_children).map((row) => row.id)),
  );
  const [focus, setFocus] = useState<string | null>(rows[0]?.id ?? null);
  const refs = useRef(new Map<string, HTMLLIElement | null>());

  const visible = visibleOutline(rows, expanded);
  const byId = useMemo(() => new Map(rows.map((row) => [row.id, row])), [rows]);

  function toggle(id: string, open?: boolean): void {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (open ?? !next.has(id)) next.add(id);
      else next.delete(id);
      return next;
    });
  }

  function moveTo(id: string | undefined): void {
    if (!id) return;
    setFocus(id);
    refs.current.get(id)?.focus();
  }

  function onKeyDown(event: KeyboardEvent<HTMLUListElement>): void {
    const current = focus ? byId.get(focus) : undefined;
    if (!current) return;
    const at = visible.findIndex((row) => row.id === current.id);
    const open = current.has_children && expanded.has(current.id);
    let handled = true;
    switch (event.key) {
      case "ArrowDown":
        moveTo(visible[at + 1]?.id);
        break;
      case "ArrowUp":
        moveTo(visible[at - 1]?.id);
        break;
      case "Home":
        moveTo(visible[0]?.id);
        break;
      case "End":
        moveTo(visible[visible.length - 1]?.id);
        break;
      case "ArrowRight":
        if (current.has_children && !open) toggle(current.id, true);
        else if (open) moveTo(children.get(current.id)?.[0]?.id);
        break;
      case "ArrowLeft":
        if (open) toggle(current.id, false);
        else moveTo(current.parent ?? undefined);
        break;
      case "Enter":
      case " ":
        if (current.has_children) toggle(current.id);
        break;
      default:
        handled = false;
    }
    if (handled) event.preventDefault();
  }

  function item(row: OutlineRow, index: number, siblings: OutlineRow[]) {
    const open = row.has_children && expanded.has(row.id);
    const kids = children.get(row.id) ?? [];
    return (
      <li
        key={row.id}
        ref={(el) => {
          refs.current.set(row.id, el);
        }}
        role="treeitem"
        data-outline={row.id}
        aria-level={row.depth}
        aria-setsize={siblings.length}
        aria-posinset={index + 1}
        aria-expanded={row.has_children ? open : undefined}
        tabIndex={row.id === focus ? 0 : -1}
        onFocus={(event) => {
          if (event.target === event.currentTarget) setFocus(row.id);
        }}
        // The ring goes on the item's own row, not round its open branch.
        className="outline-none [&:focus-visible>div]:ring-2 [&:focus-visible>div]:ring-accent"
      >
        <div
          onClick={(event) => {
            event.stopPropagation();
            setFocus(row.id);
            if (row.has_children) toggle(row.id);
          }}
          className={cx(
            "flex items-center gap-tight min-h-11 rounded-control",
            row.has_children && "cursor-pointer",
            row.depth === 1
              ? "w-fit max-w-full px-3 bg-accent/10 border border-accent/30 font-semibold text-note text-ink"
              : row.depth === 2
                ? "px-2 text-note text-ink"
                : "px-2 text-count text-ink-soft",
          )}
        >
          {row.has_children ? (
            <ChevronRightIcon
              className={cx(
                "size-icon-xs flex-none text-accent transition-transform motion-reduce:transition-none",
                open && "rotate-90",
              )}
            />
          ) : (
            <span aria-hidden="true" className="flex-none w-[0.875rem] text-center text-accent">
              •
            </span>
          )}
          <span className="min-w-0">{row.label}</span>
        </div>
        {open && kids.length ? (
          <ul role="group" className="m-0 ml-4 pl-3 list-none border-l border-line flex flex-col">
            {kids.map((kid, i) => item(kid, i, kids))}
          </ul>
        ) : null}
      </li>
    );
  }

  const top = children.get("") ?? [];
  return (
    <section data-mind-map className={CARD}>
      <ul
        role="tree"
        aria-label={t("result.notes_map_aria")}
        onKeyDown={onKeyDown}
        className="m-0 p-0 list-none flex flex-col gap-nudge"
      >
        {top.map((row, i) => item(row, i, top))}
      </ul>
    </section>
  );
}
